// FILE: src/components/AdminDevotionals.jsx
// Admin Daily Devotionals Dashboard with Firebase + AI integration

import React, { useState, useEffect } from "react";
import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  updateDoc,
  serverTimestamp,
  query,
  orderBy,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../firebase";

export default function AdminDevotionals() {
  // 🔹 REAL data only – no sample entries
  const [devotionals, setDevotionals] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const emptyDraft = {
    id: null,
    date: new Date().toISOString().slice(0, 10),
    theme: "",
    bibleVersion: "KJV",
    verse: "",
    reflection: "",
    prayer: "",
    status: "Draft",
  };

  const [editing, setEditing] = useState(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [loadingGenerate, setLoadingGenerate] = useState(false);

  // Loading & toast state
  const [loadingSave, setLoadingSave] = useState(false);
  const [loadingPublish, setLoadingPublish] = useState(false);
  const [toast, setToast] = useState({ type: "", message: "", visible: false });

  // Toast helper
  function showToast(type, message) {
    setToast({ type, message, visible: true });
    setTimeout(
      () => setToast((t) => ({ ...t, visible: false })),
      3000
    );
  }

  function Toast() {
    if (!toast.visible) return null;
    const base = "fixed right-6 top-6 z-50 px-4 py-2 rounded shadow";
    const color =
      toast.type === "success"
        ? "bg-green-600 text-white"
        : "bg-red-600 text-white";
    return <div className={`${base} ${color}`}>{toast.message}</div>;
  }

  // 🔹 Load devotionals from Firestore (live)
  useEffect(() => {
    const q = query(
      collection(db, "devotionals"),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map((d) => ({
          ...d.data(),
          id: d.id,
        }));
        setDevotionals(data);
      },
      (err) => {
        console.error("Error fetching devotionals:", err);
      }
    );

    return () => unsubscribe();
  }, []);

  function formatTimestamp(ts) {
    if (!ts) return "";
    try {
      if (ts.toDate) {
        return ts.toDate().toLocaleDateString();
      }
      return new Date(ts).toLocaleDateString();
    } catch {
      return "";
    }
  }

  function openNew() {
    setEditing({ ...emptyDraft });
    setIsPanelOpen(true);
    setPreviewOpen(false);
  }

  function openEdit(item) {
    setEditing({ ...item });
    setIsPanelOpen(true);
    setPreviewOpen(false);
  }

  function closePanel() {
    setEditing(null);
    setIsPanelOpen(false);
    setPreviewOpen(false);
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setEditing((prev) => ({ ...prev, [name]: value }));
  }

  // 🔍 Filter devotionals for the table (by theme, verse, or date)
  const filteredDevotionals = devotionals.filter((d) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    const theme = (d.theme || "").toLowerCase();
    const verse = (d.verse || "").toLowerCase();
    const dateStr = (formatTimestamp(d.createdAt) || d.date || "").toLowerCase();
    return (
      theme.includes(term) ||
      verse.includes(term) ||
      dateStr.includes(term)
    );
  });

  // 🔹 Save draft (create or update)
  async function onSaveDraft() {
    if (!editing) return;
    setLoadingSave(true);

    try {
      if (!editing.id) {
        const { id, ...rest } = editing;
        const payload = {
          ...rest,
          status: editing.status || "Draft",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };

        const docRef = await addDoc(collection(db, "devotionals"), payload);
        setEditing((prev) => ({ ...(prev || {}), id: docRef.id }));
        showToast("success", "Draft saved");
      } else {
        const ref = doc(db, "devotionals", editing.id);
        const { id, ...rest } = editing;
        const updated = { ...rest, updatedAt: serverTimestamp() };
        await updateDoc(ref, updated);
        showToast("success", "Draft updated");
      }

      setIsPanelOpen(false);
    } catch (err) {
      console.error("Error saving devotional:", err);
      showToast("error", "Failed to save. Try again.");
    } finally {
      setLoadingSave(false);
    }
  }

  // 🔹 Publish devotional
  async function onPublish() {
    if (!editing) return;
    setLoadingPublish(true);

    try {
      if (!editing.id) {
        const { id, ...rest } = editing;
        const payload = {
          ...rest,
          status: "Published",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };

        const docRef = await addDoc(collection(db, "devotionals"), payload);
        setEditing((prev) => ({ ...(prev || {}), id: docRef.id }));
        showToast("success", "Published");
      } else {
        const ref = doc(db, "devotionals", editing.id);
        const { id, ...rest } = editing;
        await updateDoc(ref, {
          ...rest,
          status: "Published",
          updatedAt: serverTimestamp(),
        });
        showToast("success", "Published");
      }

      setIsPanelOpen(false);
    } catch (err) {
      console.error("Error publishing devotional:", err);
      showToast("error", "Publish failed. Try again.");
    } finally {
      setLoadingPublish(false);
    }
  }

  // 🔹 Delete devotional
  async function onDelete(id) {
    if (!id) {
      console.error("onDelete called without id:", id);
      showToast("error", "Delete failed: missing id. Try refreshing the page.");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this devotional?"))
      return;

    try {
      await deleteDoc(doc(db, "devotionals", id));
      showToast("success", "Devotional deleted");
    } catch (err) {
      console.error("Error deleting devotional:", err);
      showToast("error", "Delete failed. Try again.");
    }
  }

  // 🔹 AI Generate devotional content
  async function onGenerate() {
    if (!editing) return;
    setLoadingGenerate(true);

    try {
      const messages = [
        {
          role: "system",
          content:
            "You are a Christian devotional writer. Produce a short devotional consisting of: a single Bible verse reference (no full verse text), a reflection about 150-220 words, and a short prayer. Return only JSON with keys: verse, reflection, prayer.",
        },
        {
          role: "user",
          content: `Create a devotional for theme="${
            editing.theme || "Faith"
          }". Output must be JSON: {"verse":"Hebrews 11:1","reflection":"...","prayer":"..."}`,
        },
      ];

      const resp = await fetch("https://api.openai.com/v1/chat/completions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${process.env.REACT_APP_OPENAI_KEY}`,
        },
        body: JSON.stringify({
          model: "gpt-4o-mini",
          messages,
          max_tokens: 500,
          temperature: 0.6,
        }),
      });

      if (!resp.ok) {
        const b = await resp.text().catch(() => "");
        throw new Error(`OpenAI error ${resp.status}: ${b}`);
      }

      const data = await resp.json();
      let assistantText = "";
      try {
        if (
          data &&
          data.choices &&
          data.choices.length > 0 &&
          data.choices[0].message &&
          data.choices[0].message.content
        ) {
          assistantText = String(data.choices[0].message.content).trim();
        }
      } catch (e) {
        assistantText = "";
      }

      if (!assistantText) {
        throw new Error("Empty AI response");
      }

      let parsed = null;
      try {
        parsed = JSON.parse(assistantText);
      } catch (err) {
        const m = assistantText.match(/\{[\s\S]*\}/);
        if (m && m[0]) parsed = JSON.parse(m[0]);
      }

      if (!parsed || !parsed.verse) {
        console.error("AI returned unexpected format:", assistantText);
        throw new Error("AI returned unexpected format");
      }

      setEditing((prev) => ({
        ...(prev || {}),
        verse: parsed.verse,
        reflection: parsed.reflection,
        prayer: parsed.prayer,
      }));
      setPreviewOpen(true);
      showToast("success", "Devotional generated by AI");
    } catch (err) {
      console.error("onGenerate error:", err);
      showToast("error", err.message || "AI generation failed");
    } finally {
      setLoadingGenerate(false);
    }
  }

  function renderStatusBadge(status) {
    const base =
      "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    if (status === "Published")
      return (
        <span className={`${base} bg-green-100 text-green-800`}>Published</span>
      );
    if (status === "Draft")
      return (
        <span className={`${base} bg-yellow-100 text-yellow-800`}>Draft</span>
      );
    return (
      <span className={`${base} bg-gray-100 text-gray-800`}>{status}</span>
    );
  }

  return (
    <div className="min-h-screen p-6 bg-gray-50">
      <Toast />
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold">
            📖 Daily Devotionals Dashboard
          </h1>
          <div className="flex gap-3">
            <input
              placeholder="Search by theme, verse or date"
              className="px-3 py-2 rounded-md border border-gray-200 bg-white text-sm w-72"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button
              onClick={openNew}
              className="inline-flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-md shadow-sm hover:bg-indigo-700"
            >
              ➕ New Devotional
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="mt-6 bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Theme
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Verse
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredDevotionals.map((d) => (
                <tr key={d.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {formatTimestamp(d.createdAt) || d.date || ""}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {d.theme}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {d.verse}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {renderStatusBadge(d.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium flex gap-2 justify-end">
                    <button
                      onClick={() => openEdit(d)}
                      className="px-3 py-1 rounded-md bg-white border border-gray-200 text-sm hover:bg-gray-50"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => onDelete(d.id)}
                      className="px-3 py-1 rounded-md bg-white border border-red-200 text-sm text-red-600 hover:bg-red-50"
                    >
                      🗑️ Delete
                    </button>
                  </td>
                </tr>
              ))}

              {devotionals.length === 0 && (
                <tr>
                  <td
                    className="px-6 py-12 text-center text-sm text-gray-500"
                    colSpan={5}
                  >
                    No devotionals yet. Create your first devotional.
                  </td>
                </tr>
              )}

              {devotionals.length > 0 && filteredDevotionals.length === 0 && (
                <tr>
                  <td
                    className="px-6 py-6 text-center text-sm text-gray-500"
                    colSpan={5}
                  >
                    No devotionals match{" "}
                    <span className="font-semibold">"{searchTerm}"</span>.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Side Panel - Create / Edit */}
        {isPanelOpen && editing && (
          <div className="fixed inset-0 z-40 flex">
            <div className="flex-1" onClick={closePanel} />
            <div className="w-full max-w-2xl bg-white shadow-xl p-6 overflow-auto">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">
                  {editing.id ? "Edit Devotional" : "New Devotional"}
                </h2>
                <button
                  onClick={closePanel}
                  className="text-sm text-gray-500"
                >
                  Close
                </button>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-4">
                <div className="flex flex-col gap-3 sm:flex-row">
                  {/* 🔹 THEME: free text + quick suggestions */}
                  <div className="flex flex-col sm:w-1/3 text-sm text-gray-600">
                    <label className="mb-1">Theme</label>
                    <input
                      name="theme"
                      value={editing.theme}
                      onChange={handleChange}
                      placeholder="e.g. God’s Faithfulness"
                      className="px-2 py-2 rounded border border-gray-200 bg-white"
                    />
                    <div className="mt-2 flex flex-wrap gap-1">
                      {["Faith", "Hope", "Love", "Prayer", "Grace", "Forgiveness"].map(
                        (t) => (
                          <button
                            key={t}
                            type="button"
                            onClick={() =>
                              setEditing((prev) => ({ ...(prev || {}), theme: t }))
                            }
                            className="px-2 py-1 text-xs rounded-full border border-gray-200 hover:bg-gray-50"
                          >
                            {t}
                          </button>
                        )
                      )}
                    </div>
                  </div>

                  <label className="flex flex-col sm:w-1/3 text-sm text-gray-600">
                    Bible Version
                    <select
                      name="bibleVersion"
                      value={editing.bibleVersion}
                      onChange={handleChange}
                      className="mt-1 px-2 py-2 rounded border border-gray-200 bg-white"
                    >
                      <option>KJV</option>
                      <option>NIV</option>
                      <option>ESV</option>
                    </select>
                  </label>

                  <label className="flex flex-col sm:w-1/3 text-sm text-gray-600">
                    Date to Publish
                    <input
                      name="date"
                      value={editing.date}
                      onChange={handleChange}
                      type="date"
                      className="mt-1 px-2 py-2 rounded border border-gray-200 bg-white"
                    />
                  </label>
                </div>

                <div>
                  <label className="block text-sm text-gray-600">
                    Verse (editable)
                  </label>
                  <input
                    name="verse"
                    value={editing.verse}
                    onChange={handleChange}
                    className="mt-1 w-full px-3 py-2 rounded border border-gray-200 bg-white"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600">
                    Reflection
                  </label>
                  <textarea
                    name="reflection"
                    value={editing.reflection}
                    onChange={handleChange}
                    rows={6}
                    className="mt-1 w-full px-3 py-2 rounded border border-gray-200 bg-white"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600">Prayer</label>
                  <textarea
                    name="prayer"
                    value={editing.prayer}
                    onChange={handleChange}
                    rows={3}
                    className="mt-1 w-full px-3 py-2 rounded border border-gray-200 bg-white"
                  />
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={onGenerate}
                    disabled={loadingGenerate}
                    className="px-4 py-2 rounded-md bg-indigo-50 border border-indigo-200 text-indigo-700 hover:bg-indigo-100 disabled:opacity-60"
                  >
                    {loadingGenerate ? "Generating…" : "Auto-generate with AI"}
                  </button>

                  <button
                    onClick={() => setPreviewOpen((p) => !p)}
                    className="px-4 py-2 rounded-md bg-white border border-gray-200"
                  >
                    Toggle Preview
                  </button>

                  <div className="ml-auto flex gap-2">
                    <button
                      onClick={onSaveDraft}
                      disabled={loadingSave}
                      className="px-4 py-2 rounded-md bg-yellow-50 border border-yellow-200 text-yellow-800 disabled:opacity-60 flex items-center gap-2"
                    >
                      {loadingSave && (
                        <svg
                          className="animate-spin h-4 w-4"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                          ></path>
                        </svg>
                      )}
                      {loadingSave ? "Saving…" : "Save Draft"}
                    </button>

                    <button
                      onClick={onPublish}
                      disabled={loadingPublish}
                      className="px-4 py-2 rounded-md bg-green-600 text-white disabled:opacity-60 flex items-center gap-2"
                    >
                      {loadingPublish && (
                        <svg
                          className="animate-spin h-4 w-4"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                          ></path>
                        </svg>
                      )}
                      {loadingPublish ? "Publishing…" : "Publish"}
                    </button>
                  </div>
                </div>

                {previewOpen && (
                  <div className="mt-4 p-4 border border-gray-100 rounded-lg bg-gray-50">
                    <h3 className="text-lg font-semibold">Preview</h3>
                    <p className="text-sm text-gray-600">
                      {editing.theme} • {editing.verse}
                    </p>
                    <div className="mt-3 space-y-3">
                      <div>
                        <p className="font-medium">Reflection</p>
                        <p className="text-sm text-gray-700">
                          {editing.reflection || "(No reflection yet)"}
                        </p>
                      </div>
                      <div>
                        <p className="font-medium">Prayer</p>
                        <p className="text-sm text-gray-700">
                          {editing.prayer || "(No prayer yet)"}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
